.root {
  display: flex;
  flex-direction: column;
}

/* ── Hero ───────────────────────────────────────────────── */
.hero {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 100dvh;
  padding: 32px 20px;
  overflow: hidden;
}

.watermark {
  position: absolute;
  top: 50%;
  right: -18%;
  width: min(90vw, 780px);
  height: auto;
  transform: translateY(-50%);
  color: var(--amber);
  opacity: 0.09;
  pointer-events: none;
}

.watermark polygon,
.watermark line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1;
}

.center {
  position: relative;
  width: min(100%, 480px);
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* Brand */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brandMark {
  width: 28px;
  height: 28px;
  color: var(--amber);
  flex-shrink: 0;
}

.brandName {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
}

.brandLink {
  margin-left: auto;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--soft);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.brandLink:hover {
  color: var(--text);
  border-bottom-color: var(--border-md);
}

/* Tagline */
.tagline {
  font-family: var(--font-display);
  font-size: clamp(1.9rem, 6vw, 2.7rem);
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.15;
  color: var(--text);
  margin: 0;
}

/* Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.label {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--soft);
}

.inputRow {
  display: flex;
  gap: 8px;
}

.inputShell {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 52px;
  background: #FFFFFF;
  border: 1px solid var(--border-md);
  border-radius: var(--radius-sm);
  transition: border-color 0.15s;
}

.inputShell:focus-within {
  border-color: var(--amber);
  box-shadow: 0 0 0 2px var(--amber);
}

.inputIcon {
  width: 18px;
  height: 18px;
  color: var(--muted);
  flex-shrink: 0;
}

.input {
  flex: 1;
  /* iOS Safari auto-zooms the page on focus for any input under 16px —
     fixed px (not rem) so it stays 16px regardless of the body's 15px
     base font-size. */
  font-size: 16px;
  color: var(--text);
  min-width: 0;
  outline: none;
}

.input::placeholder {
  color: var(--muted);
}

.submit {
  height: 52px;
  padding: 0 22px;
  background: var(--amber);
  color: #262119;
  font-size: 1rem;
  font-weight: 700;
  border-radius: var(--radius-sm);
  white-space: nowrap;
  transition: opacity 0.15s, transform 0.1s;
  flex-shrink: 0;
}

.submit:hover:not(:disabled) {
  opacity: 0.88;
}

.submit:active:not(:disabled) {
  transform: scale(0.98);
}

.submit:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.error {
  font-size: 0.9rem;
  color: var(--down);
  margin: 0;
}

/* Trust chips */
.trust {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.88rem;
  color: var(--muted);
}

.dot {
  color: var(--border-md);
}

/* Scroll cue */
.scrollCue {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  color: var(--muted);
  border: 1px solid var(--border-md);
  border-radius: 999px;
  transition: color 0.15s, border-color 0.15s;
}

.scrollCue svg {
  width: 16px;
  height: 16px;
}

.scrollCue:hover {
  color: var(--amber);
  border-color: var(--amber-border);
}

@media (prefers-reduced-motion: no-preference) {
  .scrollCue {
    animation: bob 2.2s ease-in-out infinite;
  }
}

@keyframes bob {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50%      { transform: translateX(-50%) translateY(5px); }
}

/* ── Shared section shell ───────────────────────────────── */
.section {
  padding: 88px 0;
  border-top: 1px solid var(--border);
}

.sectionInner {
  width: min(100%, 880px);
  margin: 0 auto;
  padding: 0 20px;
}

.eyebrow {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--amber-strong);
  margin-bottom: 10px;
}

.sectionTitle {
  font-family: var(--font-display);
  font-size: clamp(1.6rem, 3.8vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--text);
  margin-bottom: 40px;
}

/* ── Features ───────────────────────────────────────────── */
.featureGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}

.featureCard {
  padding: 24px;
  background: var(--surface);
  border-radius: var(--radius);
  animation: fadeUp 0.4s ease both;
  animation-delay: calc(var(--i, 0) * 70ms);
}

.featureIcon {
  width: 22px;
  height: 22px;
  color: var(--amber);
  margin-bottom: 16px;
  fill: none;
  stroke: currentColor;
}

.featureEyebrow {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--amber-strong);
  margin-bottom: 8px;
}

.featureTitle {
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 8px;
  line-height: 1.35;
}

.featureBody {
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--soft);
}

/* ── How it works ───────────────────────────────────────── */
.steps {
  display: flex;
  flex-direction: column;
  gap: 24px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.step {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}

.stepNum {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: var(--amber-strong);
  background: var(--amber-dim);
  border-radius: 999px;
}

.stepTitle {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 4px;
}

.stepBody {
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--soft);
}

/* ── Guides ─────────────────────────────────────────────── */
.guideList {
  list-style: none;
  padding: 0;
  margin: 0;
}

.guideList li {
  border-top: 1px solid var(--border);
}

.guideList li:last-child {
  border-bottom: 1px solid var(--border);
}

.guideLink {
  display: block;
  padding: 16px 4px;
  text-decoration: none;
}

.guideLink:hover .guideTitle {
  color: var(--amber-strong);
}

.guideTitle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 4px;
}

.guideBody {
  display: block;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--soft);
}

.guideArrow {
  width: 15px;
  height: 15px;
  color: var(--amber);
  transition: transform 0.15s ease;
}

.guideLink:hover .guideArrow {
  transform: translateX(3px);
}

/* ── Footer ─────────────────────────────────────────────── */
.footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 20px 40px;
}

.footerRow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 14px;
}

.footerNote {
  font-size: 0.8rem;
  color: var(--muted);
  text-align: center;
}

.footerLinks {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.footerLinks a {
  font-size: 0.85rem;
  color: var(--muted);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.footerLinks a:hover {
  color: var(--soft);
  border-bottom-color: var(--border-md);
}

.footerMark {
  width: 16px;
  height: 16px;
  color: var(--muted);
}

.footerName {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
}

@media (max-width: 480px) {
  .inputRow {
    flex-direction: column;
  }

  .submit {
    width: 100%;
  }

  .section {
    padding: 64px 0;
  }

  .sectionTitle {
    margin-bottom: 28px;
  }
}
